import React, { useState, useEffect } from 'react';

// Scoped styles. The original document set the font + scrollbar styling on
// `body`; they are re-scoped to the component root (.battery-market-map) so the
// report is self-contained and can be dropped into any route.
const STYLES = `
    .battery-market-map { font-family: 'Inter', sans-serif; }
    .battery-market-map .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .battery-market-map .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
    .battery-market-map .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
    .battery-market-map .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
`;

// ---------------------------------------------------------------------------
// Data model
// Full Tailwind class strings are kept literal (not constructed) so the JIT
// compiler can detect them.
// ---------------------------------------------------------------------------

const CATEGORIES = [
    {
        id: 'cat-1',
        number: 1,
        title: 'Critical Minerals',
        tier: 'Upstream',
        borderTop: 'border-emerald-500',
        numBadge: 'bg-emerald-100 text-emerald-700',
        tierBadge: 'bg-emerald-50 text-emerald-700',
        taxonomy: {
            intro: 'Upstream extraction, brine refining, and hard-rock processing for critical battery active elements.',
            items: [
                { label: 'Lithium Extraction & Refining:', text: ' Spodumene mining, DLE technology, battery-grade hydroxide, and specialized refining equipment (e.g., MVR).' },
                { label: 'Cobalt & Nickel Processing:', text: ' Class 1 HPAL nickel, refined cobalt sulfate.' },
                { label: 'Natural & Synthetic Graphite:', text: ' Anode precursor feedstocks and high-purity spherical graphite.' },
            ],
        },
        ecosystem: [
            {
                heading: 'Lithium Extraction & Refining',
                companies: [
                    { name: 'Albemarle', challenger: false, domain: 'albemarle.com' },
                    { name: 'SQM', challenger: false, domain: 'sqm.com' },
                    { name: 'Tianqi Lithium', challenger: false, domain: 'tianqilithium.com' },
                    { name: 'Ganfeng Lithium', challenger: false, domain: 'ganfenglithium.com' },
                    { name: 'MinRes', challenger: false, domain: 'mineralresources.com.au' },
                    { name: 'Lilac Solutions', challenger: true, domain: 'lilacsolutions.com' },
                    { name: 'EnergyX', challenger: true, domain: 'energyx.com' },
                    { name: 'WJ Korea', challenger: true, domain: 'wjkorea.com' },
                ],
            },
            {
                heading: 'Cobalt & Nickel Processing',
                companies: [
                    { name: 'Glencore', challenger: false, domain: 'glencore.com' },
                    { name: 'Vale', challenger: false, domain: 'vale.com' },
                    { name: 'Jinchuan Group', challenger: false, domain: 'jinchuan.com.cn' },
                    { name: 'Zhejiang Huayou', challenger: false, domain: 'huayou.com' },
                    { name: 'Sherritt', challenger: false, domain: 'sherritt.com' },
                    { name: 'Cobalt Blue', challenger: true, domain: 'cobaltblueholdings.com' },
                    { name: 'QPM', challenger: true, domain: 'qpmetals.com.au' },
                ],
            },
            {
                heading: 'Graphite Supply',
                companies: [
                    { name: 'Syrah Resources', challenger: false, domain: 'syrahresources.com.au' },
                    { name: 'Northern Graphite', challenger: false, domain: 'northerngraphite.com' },
                    { name: 'BTR New Material', challenger: false, domain: 'btrchina.com' },
                    { name: 'Ningbo Shanshan', challenger: false, domain: 'shanshan.com' },
                    { name: 'Putailai', challenger: false, domain: 'putailai.com' },
                    { name: 'SGL Carbon', challenger: false, domain: 'sglcarbon.com' },
                    { name: 'Novonix', challenger: true, domain: 'novonixgroup.com' },
                ],
            },
        ],
        gcc: [
            { type: 'priority', text: 'WJ Korea target: Supply MVR refining trains for oilfield brines, desalination reject water, and lithium refineries.' },
            {
                type: 'group',
                heading: 'Sovereign Mining & Energy',
                companies: [
                    { name: "Ma'aden", challenger: false, domain: 'maaden.com.sa' },
                    { name: 'Saudi Aramco', challenger: false, domain: 'aramco.com' },
                    { name: 'Manara Minerals', challenger: false, domain: 'manaraminerals.com' },
                    { name: "TA'ZIZ (UAE)", challenger: false, domain: 'taziz.com' },
                    { name: 'QIMC (Qatar)', challenger: false, domain: 'qimc.com.qa' },
                ],
            },
            {
                type: 'group',
                heading: 'Desalination, Refining & Financing',
                companies: [
                    { name: 'SWCC (KSA)', challenger: false, domain: 'swcc.gov.sa' },
                    { name: 'KAUST', challenger: true, domain: 'kaust.edu.sa' },
                    { name: 'NIDC (KSA 75% Fin)', challenger: false, domain: 'nidc.gov.sa' },
                    { name: 'Titan Lithium (UAE)', challenger: true, domain: 'titanlithium.ae' },
                ],
            },
        ],
    },
    {
        id: 'cat-2',
        number: 2,
        title: 'Material Synthesis',
        tier: 'Midstream',
        borderTop: 'border-sky-500',
        numBadge: 'bg-sky-100 text-sky-700',
        tierBadge: 'bg-sky-50 text-sky-700',
        taxonomy: {
            intro: 'Chemical refinement of active materials required for cell assembly.',
            items: [
                { label: 'Cathode Active Materials (CAM):', text: ' High-nickel NCM, NCA, and LFP powder formulations.' },
                { label: 'Anode Active Materials (AAM):', text: ' Silicon-blended anodes and pure silicon nanowires.' },
                { label: 'Electrolytes & Separators:', text: ' Liquid lithium salts and wet/dry process polyolefin separators.' },
            ],
        },
        ecosystem: [
            {
                heading: 'Cathode Materials (CAM)',
                companies: [
                    { name: 'Umicore', challenger: false, domain: 'umicore.com' },
                    { name: 'BASF', challenger: false, domain: 'basf.com' },
                    { name: 'POSCO Future M', challenger: false, domain: 'posco.com' },
                    { name: 'EcoPro BM', challenger: false, domain: 'ecopro.co.kr' },
                    { name: 'LG Chem', challenger: false, domain: 'lgchem.com' },
                    { name: 'Sumitomo', challenger: false, domain: 'smm.co.jp' },
                    { name: 'Redwood', challenger: true, domain: 'redwoodmaterials.com' },
                ],
            },
            {
                heading: 'Anode Materials (AAM)',
                companies: [
                    { name: 'BTR', challenger: false, domain: 'btrchina.com' },
                    { name: 'Ningbo Shanshan', challenger: false, domain: 'shanshan.com' },
                    { name: 'Resonac', challenger: false, domain: 'resonac.com' },
                    { name: 'POSCO', challenger: false, domain: 'posco.com' },
                    { name: 'Sila Nano', challenger: true, domain: 'silanano.com' },
                    { name: 'Group14 Tech', challenger: true, domain: 'group14.technology' },
                    { name: 'Amprius', challenger: true, domain: 'amprius.com' },
                ],
            },
            {
                heading: 'Electrolytes & Separators',
                companies: [
                    { name: 'Semcorp', challenger: false, domain: 'semcorp-global.com' },
                    { name: 'Asahi Kasei', challenger: false, domain: 'asahi-kasei.com' },
                    { name: 'Toray', challenger: false, domain: 'toray.com' },
                    { name: 'Guangzhou Tinci', challenger: false, domain: 'tinci.com' },
                    { name: 'Enchem', challenger: false, domain: 'enchem.net' },
                    { name: 'Capchem', challenger: false, domain: 'capchem.com' },
                    { name: 'Senior Tech', challenger: false, domain: 'senior-china.com' },
                ],
            },
        ],
        gcc: [
            { type: 'note', text: 'Establish local modular skid assembly and Joint Ventures within these primary industrial zones.' },
            {
                type: 'group',
                heading: 'Industrial Free Zones',
                companies: [
                    { name: 'KEZAD Group (UAE)', challenger: false, domain: 'kezadgroup.com' },
                    { name: 'JAFZA (UAE)', challenger: false, domain: 'jafza.ae' },
                    { name: 'RCJY (Yanbu)', challenger: false, domain: 'rcjy.gov.sa' },
                    { name: 'KAEC (KSA)', challenger: false, domain: 'kaec.net' },
                    { name: 'Manateq (Qatar)', challenger: false, domain: 'manateq.qa' },
                ],
            },
            {
                type: 'group',
                heading: 'Government Enablers',
                companies: [
                    { name: 'MoIAT (UAE)', challenger: false, domain: 'moiat.gov.ae' },
                    { name: 'MIMR (KSA)', challenger: false, domain: 'mim.gov.sa' },
                ],
            },
        ],
    },
    {
        id: 'cat-3',
        number: 3,
        title: 'Cell Manufacturing',
        tier: 'Midstream',
        borderTop: 'border-indigo-500',
        numBadge: 'bg-indigo-100 text-indigo-700',
        tierBadge: 'bg-indigo-50 text-indigo-700',
        taxonomy: {
            intro: 'Giga-scale cell fabrication, solid-state innovation, and pack control.',
            items: [
                { label: 'Tier-1 Cell Manufacturers:', text: ' High-volume pouch, prismatic, and cylindrical cells.' },
                { label: 'Solid-State Challengers:', text: ' All-solid-state and ultra-fast charging architectures.' },
                { label: 'BMS & Assembly:', text: ' Thermal management and cell-to-pack (CTP) integration.' },
            ],
        },
        ecosystem: [
            {
                heading: 'Tier-1 Cell Mfrs',
                companies: [
                    { name: 'CATL', challenger: false, domain: 'catl.com' },
                    { name: 'BYD', challenger: false, domain: 'byd.com' },
                    { name: 'LG Energy', challenger: false, domain: 'lgensol.com' },
                    { name: 'Samsung SDI', challenger: false, domain: 'samsungsdi.com' },
                    { name: 'Panasonic', challenger: false, domain: 'panasonic.com' },
                    { name: 'SK On', challenger: false, domain: 'sk-on.com' },
                    { name: 'CALB', challenger: false, domain: 'calb-tech.com' },
                ],
            },
            {
                heading: 'Solid-State & Next-Gen',
                companies: [
                    { name: 'QuantumScape', challenger: true, domain: 'quantumscape.com' },
                    { name: 'Solid Power', challenger: true, domain: 'solidpowerbattery.com' },
                    { name: 'Factorial', challenger: true, domain: 'factorialenergy.com' },
                    { name: 'StoreDot', challenger: true, domain: 'storedot.com' },
                    { name: 'HiNa Battery', challenger: true, domain: 'hinabattery.com' },
                    { name: 'Natron Energy', challenger: true, domain: 'natron.energy' },
                    { name: 'ONE', challenger: true, domain: 'one.ai' },
                ],
            },
            {
                heading: 'BMS & Pack Assembly',
                companies: [
                    { name: 'Valmet Auto', challenger: false, domain: 'valmet-automotive.com' },
                    { name: 'BorgWarner', challenger: false, domain: 'borgwarner.com' },
                    { name: 'Webasto', challenger: false, domain: 'webasto-group.com' },
                    { name: 'Sensata', challenger: false, domain: 'sensata.com' },
                    { name: 'Eaton', challenger: false, domain: 'eaton.com' },
                    { name: 'NXP', challenger: false, domain: 'nxp.com' },
                    { name: 'ION Energy', challenger: true, domain: 'ionenergy.co' },
                ],
            },
        ],
        gcc: [
            { type: 'note', text: 'Secure co-investment and project financing for sovereign JV localized capacity building.' },
            {
                type: 'group',
                heading: 'Sovereign Wealth & Financing',
                companies: [
                    { name: 'PIF (KSA)', challenger: false, domain: 'pif.gov.sa' },
                    { name: 'SIDF (KSA)', challenger: false, domain: 'sidf.gov.sa' },
                    { name: 'ADQ (UAE)', challenger: false, domain: 'adq.ae' },
                    { name: 'Mubadala', challenger: false, domain: 'mubadala.com' },
                ],
            },
        ],
    },
    {
        id: 'cat-4',
        number: 4,
        title: 'Application & ESS',
        tier: 'Downstream',
        borderTop: 'border-amber-500',
        numBadge: 'bg-amber-100 text-amber-700',
        tierBadge: 'bg-amber-50 text-amber-700',
        taxonomy: {
            intro: 'Deployment across electric mobility, utility grid stabilization, and portable devices.',
            items: [
                { label: 'Automotive EVs:', text: ' Passenger EVs, commercial trucks, fleets.' },
                { label: 'Utility-Scale ESS:', text: ' Grid peaking, renewable buffering, microgrids.' },
                { label: 'Portable & Consumer:', text: ' High-density tools, laptops, devices.' },
            ],
        },
        ecosystem: [
            {
                heading: 'Automotive EVs',
                companies: [
                    { name: 'Tesla', challenger: false, domain: 'tesla.com' },
                    { name: 'Hyundai', challenger: false, domain: 'hyundai.com' },
                    { name: 'Volkswagen', challenger: false, domain: 'volkswagen-group.com' },
                    { name: 'Rivian', challenger: true, domain: 'rivian.com' },
                    { name: 'NIO', challenger: true, domain: 'nio.com' },
                    { name: 'Li Auto', challenger: true, domain: 'lixiang.com' },
                ],
            },
            {
                heading: 'Utility & C&I ESS',
                companies: [
                    { name: 'Fluence', challenger: false, domain: 'fluenceenergy.com' },
                    { name: 'Powin', challenger: false, domain: 'powin.com' },
                    { name: 'Wärtsilä', challenger: false, domain: 'wartsila.com' },
                    { name: 'Enphase', challenger: false, domain: 'enphase.com' },
                    { name: 'Sungrow', challenger: false, domain: 'sungrowpower.com' },
                    { name: 'Form Energy', challenger: true, domain: 'formenergy.com' },
                ],
            },
            {
                heading: 'Portable Electronics',
                companies: [
                    { name: 'Murata Mfg', challenger: false, domain: 'murata.com' },
                    { name: 'ATL', challenger: false, domain: 'atlbattery.com' },
                    { name: 'Varta', challenger: false, domain: 'varta-ag.com' },
                    { name: 'Duracell', challenger: false, domain: 'duracell.com' },
                    { name: 'Energizer', challenger: false, domain: 'energizer.com' },
                    { name: 'EnerSys', challenger: false, domain: 'enersys.com' },
                    { name: 'EVE Energy', challenger: false, domain: 'evebattery.com' },
                ],
            },
        ],
        gcc: [
            { type: 'note', text: 'End-users driving the localized demand for battery packs and ESS infrastructure.' },
            {
                type: 'group',
                heading: 'Local EV OEMs (KSA)',
                companies: [
                    { name: 'Ceer Motors', challenger: true, domain: 'ceermotors.com' },
                    { name: 'Lucid Motors', challenger: false, domain: 'lucidmotors.com' },
                ],
            },
            {
                type: 'group',
                heading: 'Grid & Mega-Projects',
                companies: [
                    { name: 'NEOM (Enowa)', challenger: false, domain: 'enowa.neom.com' },
                    { name: 'ACWA Power', challenger: false, domain: 'acwapower.com' },
                    { name: 'DEWA (UAE)', challenger: false, domain: 'dewa.gov.ae' },
                ],
            },
        ],
    },
    {
        id: 'cat-5',
        number: 5,
        title: 'Circular Economy',
        tier: 'Closed Loop',
        borderTop: 'border-rose-500',
        numBadge: 'bg-rose-100 text-rose-700',
        tierBadge: 'bg-rose-50 text-rose-700',
        taxonomy: {
            intro: 'Urban mining, black mass processing, and second-life battery repurposing.',
            items: [
                { label: 'Hydro & Direct Recycling:', text: ' Chemical recovery of lithium, nickel, cobalt from black mass. MVR required for ZLD.' },
                { label: 'Second-Life Repurposing:', text: ' Grading degraded EV packs for stationary storage.' },
            ],
        },
        ecosystem: [
            {
                heading: 'Hydrometallurgical Recycling',
                companies: [
                    { name: 'Li-Cycle', challenger: true, domain: 'li-cycle.com' },
                    { name: 'Redwood', challenger: true, domain: 'redwoodmaterials.com' },
                    { name: 'Ascend Elements', challenger: true, domain: 'ascendelements.com' },
                    { name: 'Cirba Solutions', challenger: true, domain: 'cirbasolutions.com' },
                    { name: 'Attero', challenger: true, domain: 'attero.in' },
                    { name: 'Fortum', challenger: false, domain: 'fortum.com' },
                    { name: 'Green Li-ion', challenger: true, domain: 'greenli-ion.com' },
                    { name: 'WJ Korea', challenger: true, domain: 'wjkorea.com' },
                ],
            },
            {
                heading: 'Second-Life Repurposing',
                companies: [
                    { name: 'Connected Energy', challenger: true, domain: 'connectedenergy.co.uk' },
                    { name: 'B2U Storage', challenger: true, domain: 'b2ustorage.com' },
                    { name: 'Moment Energy', challenger: true, domain: 'momentenergy.com' },
                    { name: 'Smartville', challenger: true, domain: 'smartville.io' },
                    { name: '4R Energy', challenger: false, domain: '4r-energy.com' },
                    { name: 'MB Energy', challenger: false, domain: 'mercedes-benz-energy.com' },
                ],
            },
        ],
        gcc: [
            { type: 'priority', text: 'WJ Korea target: Supply ZLD and sodium sulfate recovery systems for regional black mass recycling plants.' },
            {
                type: 'group',
                heading: 'Recycling & ZLD Champions',
                companies: [
                    { name: 'Witthal Gulf (UAE)', challenger: true, domain: 'witthal.com' },
                    { name: 'SABIC (KSA)', challenger: false, domain: 'sabic.com' },
                    { name: 'Petro Rabigh (KSA)', challenger: false, domain: 'petrorabigh.com' },
                    { name: 'QatarEnergy', challenger: false, domain: 'qatarenergy.qa' },
                    { name: 'Dubal Holding (UAE)', challenger: false, domain: 'dubalholding.ae' },
                ],
            },
            {
                type: 'group',
                heading: 'Environmental Mandates',
                companies: [
                    { name: 'SIRC (KSA)', challenger: false, domain: 'sirc.sa' },
                ],
            },
        ],
    },
];

// ---------------------------------------------------------------------------
// Presentational sub-components
// ---------------------------------------------------------------------------

function CompanyChip({ company, onOpen }) {
    const { name, challenger, domain } = company;
    const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

    const base =
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium cursor-pointer transition-all shadow-2xs group ';
    const themed =
        name === 'WJ Korea'
            ? 'bg-indigo-50 border border-indigo-400 text-indigo-900 hover:bg-indigo-100 hover:border-indigo-500 ring-1 ring-indigo-200/50 shadow-sm'
            : 'bg-slate-100 border border-slate-200 text-slate-800 hover:bg-slate-200 hover:border-slate-300';

    return (
        <button type="button" className={base + themed} onClick={() => onOpen(company)}>
            <img
                src={faviconUrl}
                alt={name}
                className="w-3.5 h-3.5 object-contain rounded-xs opacity-80 group-hover:opacity-100"
                onError={(e) => {
                    e.currentTarget.style.display = 'none';
                }}
            />
            <span>{name}</span>
            {challenger && (
                <span className="text-amber-500 font-bold ml-0.5" title="Emerging Challenger">
                    *
                </span>
            )}
        </button>
    );
}

function CompanyGroup({ heading, companies, onOpen }) {
    return (
        <div>
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">{heading}</h4>
            <div className="flex flex-wrap gap-1.5">
                {companies.map((c, i) => (
                    <CompanyChip key={`${c.name}-${i}`} company={c} onOpen={onOpen} />
                ))}
            </div>
        </div>
    );
}

function PriorityCallout({ text }) {
    return (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-emerald-800 font-bold text-xs uppercase tracking-wider mb-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                        fillRule="evenodd"
                        d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                        clipRule="evenodd"
                    />
                </svg>
                Primary Priority
            </div>
            <p className="text-[11px] text-emerald-700">{text}</p>
        </div>
    );
}

function CategoryCard({ category, view, onOpen }) {
    return (
        <div
            className={`category-card bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 border-t-4 ${category.borderTop} border-x border-b border-slate-200 p-5 flex flex-col justify-start h-full`}
        >
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full ${category.numBadge} font-bold text-xs flex items-center justify-center`}>
                        {category.number}
                    </span>
                    <h2 className="text-base font-bold text-slate-900 tracking-tight">{category.title}</h2>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${category.tierBadge}`}>
                    {category.tier}
                </span>
            </div>

            {/* Taxonomy view */}
            {view === 'taxonomy' && (
                <div className="text-sm text-slate-700 space-y-4">
                    <p className="italic text-slate-500">{category.taxonomy.intro}</p>
                    <div className="space-y-2">
                        <div className="font-semibold text-slate-900">Key Sub-segments:</div>
                        <ul className="list-disc pl-4 space-y-1.5 text-slate-700">
                            {category.taxonomy.items.map((item, i) => (
                                <li key={i}>
                                    <strong>{item.label}</strong>
                                    {item.text}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Ecosystem view */}
            {view === 'ecosystem' && (
                <div className="flex flex-col gap-5">
                    {category.ecosystem.map((group, i) => (
                        <CompanyGroup key={i} heading={group.heading} companies={group.companies} onOpen={onOpen} />
                    ))}
                </div>
            )}

            {/* GCC view */}
            {view === 'gcc' && (
                <div className="flex flex-col gap-5">
                    {category.gcc.map((block, i) => {
                        if (block.type === 'priority') return <PriorityCallout key={i} text={block.text} />;
                        if (block.type === 'note')
                            return (
                                <div key={i} className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                    <p className="text-[11px] text-slate-600">{block.text}</p>
                                </div>
                            );
                        return <CompanyGroup key={i} heading={block.heading} companies={block.companies} onOpen={onOpen} />;
                    })}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// WJ Strategy view
// ---------------------------------------------------------------------------

function WJStrategy() {
    return (
        <div className="flex flex-col gap-6 w-full">
            {/* Strategic Pivot Banner */}
            <div className="bg-gradient-to-r from-indigo-900 to-slate-800 rounded-xl p-6 md:p-8 text-white shadow-md border border-indigo-950 relative overflow-hidden">
                <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-indigo-500 rounded-full blur-3xl opacity-20"></div>
                <h2 className="text-xs font-bold uppercase tracking-widest text-indigo-300 mb-2">The Paradigm Shift</h2>
                <p className="text-xl md:text-2xl font-light leading-snug mb-5">
                    From <span className="font-bold line-through text-slate-400">Imported Equipment Vendor</span> to{' '}
                    <span className="font-bold text-white">Sovereign Industrial &amp; Green-Tech Partner</span>.
                </p>
                <div className="space-y-3">
                    <p className="text-sm text-indigo-100 max-w-4xl opacity-95">
                        <strong className="text-white">Current Baseline:</strong> WJ Korea already holds a valuable UAE trading footprint,
                        actively distributing critical battery chemicals including Anhydrous Sodium Sulfate, Sodium Carbonate, and Lithium
                        Salts.
                    </p>
                    <p className="text-sm text-indigo-100 max-w-4xl opacity-95">
                        <strong className="text-white">The Industrial Pivot:</strong> Deploying proprietary MVR (Mechanical Vapor
                        Recompression) crystallization and{' '}
                        <strong className="text-white border-b border-indigo-400">Rotary Kilns</strong> to enable GCC nations (KSA, UAE,
                        Qatar) to process domestic brines and recycling waste into battery-grade minerals locally with absolute Zero Liquid
                        Discharge (ZLD).
                    </p>
                </div>
            </div>

            {/* 3-Track GCC Partner Matrix */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 overflow-hidden">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6">
                    WJ Korea GCC Partner Matrix (Sovereign Expansion)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* UAE Track */}
                    <div className="bg-slate-50 border-t-4 border-t-sky-500 border-x border-b border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                        <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <span className="text-xl">🇦🇪</span> UAE Track
                        </h4>
                        <div className="space-y-4 text-sm text-slate-700">
                            <div>
                                <strong className="text-slate-900 block text-xs uppercase tracking-wide mb-1.5">
                                    Industrial &amp; Infrastructure
                                </strong>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li>
                                        <strong>KEZAD Group:</strong> Assembly hub &amp; refining facility land.
                                    </li>
                                    <li>
                                        <strong>JAFZA (DP World):</strong> Warehousing &amp; re-export logistics.
                                    </li>
                                </ul>
                            </div>
                            <div className="pt-2 border-t border-slate-200">
                                <strong className="text-slate-900 block text-xs uppercase tracking-wide mb-1.5">Chemical Champions</strong>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li>
                                        <strong>TA'ZIZ (ADNOC/ADQ):</strong> Massive world-scale MVR evaporation integration.
                                    </li>
                                    <li>
                                        <strong>Dubal Holding:</strong> Industrial metals &amp; recycling backing.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* KSA Track */}
                    <div className="bg-slate-50 border-t-4 border-t-emerald-500 border-x border-b border-slate-200 p-5 rounded-lg relative shadow-[0_0_15px_rgba(16,185,129,0.15)] ring-1 ring-emerald-200">
                        <div className="absolute -top-3 right-4 bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-sm uppercase shadow-sm">
                            Primary EV Target
                        </div>
                        <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <span className="text-xl">🇸🇦</span> Saudi Arabia Track
                        </h4>
                        <div className="space-y-4 text-sm text-slate-700">
                            <div>
                                <strong className="text-slate-900 block text-xs uppercase tracking-wide mb-1.5">
                                    Mining &amp; Chemical Giants
                                </strong>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li>
                                        <strong>Ma'aden:</strong> National mining champion (lithium/nickel refining).
                                    </li>
                                    <li>
                                        <strong>SABIC &amp; Petro Rabigh:</strong> Large-scale MVR for ZLD reuse.
                                    </li>
                                </ul>
                            </div>
                            <div className="pt-2 border-t border-slate-200">
                                <strong className="text-slate-900 block text-xs uppercase tracking-wide mb-1.5">
                                    Incentives &amp; EV Ecosystem
                                </strong>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li>
                                        <strong className="text-emerald-700 font-bold bg-emerald-100 px-1 rounded">NIDC:</strong> Up to 75%
                                        debt financing for local assembly.
                                    </li>
                                    <li>
                                        <strong>RCJY:</strong> Ready Jubail/Yanbu industrial land.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Qatar Track */}
                    <div className="bg-slate-50 border-t-4 border-t-purple-500 border-x border-b border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                        <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <span className="text-xl">🇶🇦</span> Qatar Track
                        </h4>
                        <div className="space-y-4 text-sm text-slate-700">
                            <div>
                                <strong className="text-slate-900 block text-xs uppercase tracking-wide mb-1.5">State-Backed Partners</strong>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li>
                                        <strong>QIMC:</strong> Premier JV partner for chemical/equipment manufacturing.
                                    </li>
                                    <li>
                                        <strong>QatarEnergy &amp; Q-Chem:</strong> Heavy industrial water recycling &amp; salt processing
                                        demand.
                                    </li>
                                    <li>
                                        <strong>Manateq:</strong> Special economic zone incentives and land.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Frontier & KRINS Callout */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-5 shadow-sm">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center shrink-0 shadow-md">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                        </svg>
                    </div>
                    <div>
                        <h4 className="font-extrabold text-indigo-900 text-base">Strategic Advisory &amp; Execution Partner</h4>
                        <p className="text-sm text-indigo-800 mt-1 leading-snug">
                            <strong className="text-indigo-950 font-black">Frontier (Abu Dhabi HQ)</strong> and{' '}
                            <strong className="text-indigo-950 font-black">KRINS</strong> serve as WJ Korea's exclusive GCC Cross-Border
                            Strategy &amp; JV Advisors—deployed to structure these sovereign partnerships, secure government incentives, and
                            aggressively execute regional expansion.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Company modal
// ---------------------------------------------------------------------------

function CompanyModal({ company, onClose }) {
    if (!company) return null;
    const { name, challenger, domain } = company;
    const logoUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

    return (
        <div
            className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <img src={logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-md border border-slate-100 bg-white p-1" />
                        <div>
                            <h3 className="text-lg font-extrabold text-slate-900 leading-tight">{name}</h3>
                            {challenger ? (
                                <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md mt-0.5 bg-amber-100 text-amber-800 border border-amber-200">
                                    Emerging Challenger / Focus
                                </span>
                            ) : (
                                <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md mt-0.5 bg-slate-100 text-slate-700 border border-slate-200">
                                    Established Incumbent
                                </span>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="space-y-3 text-xs text-slate-600">
                    <div>
                        <span className="font-bold text-slate-700">Official Website:</span>
                        <a
                            href={`https://${domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:underline ml-1"
                        >
                            {domain}
                        </a>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-slate-500">
                        <p>
                            Entity tracked within the global secondary battery ecosystem. Targeted for potential localized capacity or
                            off-take alignment.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Toggle bar
// ---------------------------------------------------------------------------

const TOGGLE_BASE = 'px-3 py-1 rounded-md text-xs font-bold transition-all';
const TOGGLE_INACTIVE = `${TOGGLE_BASE} text-slate-500 hover:text-slate-800`;

function ViewToggle({ view, setView }) {
    return (
        <div className="bg-slate-100 p-1 rounded-lg flex items-center border border-slate-200">
            <button
                onClick={() => setView('ecosystem')}
                className={view === 'ecosystem' ? `${TOGGLE_BASE} bg-white text-indigo-600 shadow-xs` : TOGGLE_INACTIVE}
            >
                Ecosystem
            </button>
            <button
                onClick={() => setView('gcc')}
                className={
                    view === 'gcc'
                        ? `${TOGGLE_BASE} bg-emerald-100 text-emerald-800 shadow-xs flex items-center gap-1 border border-emerald-200`
                        : `${TOGGLE_INACTIVE} flex items-center gap-1`
                }
            >
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span> GCC Partners
            </button>
            <button
                onClick={() => setView('taxonomy')}
                className={view === 'taxonomy' ? `${TOGGLE_BASE} bg-white text-indigo-600 shadow-xs` : TOGGLE_INACTIVE}
            >
                Industry
            </button>
            <div className="w-px h-4 bg-slate-300 mx-1"></div>
            <button
                onClick={() => setView('wj')}
                className={
                    view === 'wj'
                        ? `${TOGGLE_BASE} bg-indigo-50 text-indigo-700 shadow-xs flex items-center gap-1 ring-1 ring-indigo-200`
                        : `${TOGGLE_INACTIVE} flex items-center gap-1`
                }
            >
                <span className="w-2 h-2 rounded-full bg-indigo-500"></span> WJ Strategy
            </button>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

export default function BatteryMarketMap() {
    const [view, setView] = useState('ecosystem'); // 'ecosystem' | 'gcc' | 'taxonomy' | 'wj'
    const [modalCompany, setModalCompany] = useState(null);

    // Close the modal on Escape.
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setModalCompany(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    return (
        <div className="battery-market-map min-h-full flex flex-col text-slate-900 bg-slate-50 antialiased selection:bg-indigo-500 selection:text-white">
            <style>{STYLES}</style>

            <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-xs">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                                    The Value Chain
                                </span>
                                <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Strategic Map</span>
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-1">
                                Secondary Battery <span className="text-indigo-600">Market Map</span>
                            </h1>
                            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                                Multi-level taxonomy, competitive ecosystem, and GCC localization partnerships.
                            </p>
                        </div>
                    </div>
                </div>
            </header>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2 w-full">
                <div className="bg-white p-4 rounded-xl shadow-xs border border-slate-200 flex flex-col sm:flex-row items-center justify-end gap-4">
                    <ViewToggle view={view} setView={setView} />
                </div>
            </section>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-grow w-full">
                {view === 'wj' ? (
                    <WJStrategy />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {CATEGORIES.map((category) => (
                            <CategoryCard key={category.id} category={category} view={view} onOpen={setModalCompany} />
                        ))}
                    </div>
                )}
            </main>

            <CompanyModal company={modalCompany} onClose={() => setModalCompany(null)} />

            <footer className="text-center text-slate-400 text-xs mt-12 pb-6">
                © 2026 Market Landscape. Generated by{' '}
                <a
                    href="https://strategyandagents.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-slate-500 hover:text-indigo-600 transition-colors duration-200"
                >
                    Frontier | Strategy &amp; Agents
                </a>
            </footer>
        </div>
    );
}
